import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import React from 'react';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  autoPickTier,
  evaluateTierWindow,
  isTierEligible,
  normalizePriceTiers,
} from '../../util/priceTiers';

import css from './PriceTierPicker.module.css';

const { Money } = sdkTypes;

const statusMessageId = status => {
  if (status === 'ended') {
    return 'PriceTierPicker.ended';
  }
  if (status === 'not_started') {
    return 'PriceTierPicker.notStarted';
  }
  return 'PriceTierPicker.soldOut';
};

/**
 * Buyer price-tier selector. Rendered on checkout, where the buyer chooses an option.
 */
const PriceTierPicker = props => {
  const {
    listing,
    selectedTierId,
    onSelect,
    intl,
    bookingStart,
    unitsSoldByTierId,
    unitsAvailabilityKnown = true,
    className,
    showAll,
  } = props;

  const catalog = normalizePriceTiers(listing?.attributes?.publicData?.priceTiers);
  if (!catalog) {
    return null;
  }

  const listingType = listing?.attributes?.publicData?.listingType;
  const ctx = {
    now: new Date(),
    bookingStart,
    listingType,
    publishedAt: listing?.attributes?.createdAt || null,
    unitsSoldByTierId: unitsSoldByTierId || {},
  };

  const autoPicked = autoPickTier(catalog.items, ctx);
  // Do not visually auto-select when the parent passed no choice (sold-out
  // notice, or listing-page selection still loading). Callers that want the
  // cheapest option pass that id explicitly.
  const selectedId = selectedTierId || null;
  const hasEligibleTier = !!autoPicked;

  const visibleTiers = catalog.items.filter(tier => {
    if (showAll || catalog.showUnavailable) {
      return true;
    }
    return isTierEligible(tier, ctx);
  });

  return (
    <div className={classNames(css.root, className)} data-testid="price-tiers-picker">
      <h2 className={css.heading}>
        <FormattedMessage id="PriceTierPicker.heading" />
      </h2>
      {!hasEligibleTier ? (
        <p className={css.noneAvailable} data-testid="price-tiers-none-available">
          <FormattedMessage id="PriceTierPicker.noneAvailable" />
        </p>
      ) : null}
      {visibleTiers.length > 0 ? (
        <ul className={css.list} role="list">
          {visibleTiers.map(tier => {
            const eligible = isTierEligible(tier, ctx);
            const windowState = evaluateTierWindow(tier, ctx);
            const soldOut =
              unitsAvailabilityKnown &&
              tier.maxUnits != null &&
              Number(ctx.unitsSoldByTierId?.[tier.id] || 0) >= tier.maxUnits;
            const canSelect = eligible;
            const status = soldOut ? 'sold_out' : windowState;
            const selected = tier.id === selectedId;
            const price = formatMoney(intl, new Money(tier.amount, tier.currency));
            const unitsLeft =
              unitsAvailabilityKnown && tier.showUnitsLeft && tier.maxUnits != null
                ? Math.max(0, tier.maxUnits - Number(ctx.unitsSoldByTierId?.[tier.id] || 0))
                : null;
            const compareAt =
              catalog.showCompareAt && listing?.attributes?.price?.amount > tier.amount
                ? formatMoney(intl, listing.attributes.price)
                : null;

            return (
              <li key={tier.id}>
                <button
                  type="button"
                  className={classNames(css.row, {
                    [css.rowSelected]: selected,
                    [css.rowDisabled]: !canSelect,
                  })}
                  data-testid={`price-tier-option-${tier.id}`}
                  disabled={!canSelect}
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => canSelect && onSelect && onSelect(tier.id)}
                >
                  <span className={css.copy}>
                    <span className={css.name}>{tier.name}</span>
                    {tier.description ? (
                      <span className={css.description}>{tier.description}</span>
                    ) : null}
                    {unitsLeft != null ? (
                      <span className={css.unitsLeft}>
                        <FormattedMessage
                          id="PriceTierPicker.unitsLeft"
                          values={{ count: unitsLeft }}
                        />
                      </span>
                    ) : null}
                    {!canSelect ? (
                      <span className={css.badge}>
                        <FormattedMessage id={statusMessageId(status)} />
                      </span>
                    ) : null}
                  </span>
                  <span className={css.priceBlock}>
                    {compareAt ? <span className={css.compareAt}>{compareAt}</span> : null}
                    <span className={css.price}>{price}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

PriceTierPicker.defaultProps = {
  selectedTierId: null,
  onSelect: null,
  bookingStart: null,
  unitsSoldByTierId: null,
  unitsAvailabilityKnown: true,
  className: null,
  showAll: false,
};

PriceTierPicker.propTypes = {
  listing: object.isRequired,
  selectedTierId: string,
  onSelect: func,
  intl: intlShape.isRequired,
  bookingStart: object,
  unitsSoldByTierId: object,
  unitsAvailabilityKnown: bool,
  className: string,
  showAll: bool,
};

export default PriceTierPicker;
